<template>
  <div class="content-block">
    <div class="app-buttons">
      <dx-button
        id="create"
        icon="plus"
        type="normal"
        :text="$t('general.create')"
        @click="onCreate"
      />
    </div>

    <dx-data-grid
      v-if="dataSourceConfig"
      class="data-grid dx-card"
      :data-source="dataSourceConfig"
      :remote-operations="remoteOperations"
      :syncLookupFilterValues="false"
      :height="gridHeight"
      @content-ready="onContentReady"
      @row-click="onRowClick"
      @context-menu-preparing="addMenuItems"
      @editor-preparing="onEditorPreparing"
      @initialized=initializedGrid
      ref="dataGrid"
    >
      <dx-paging :enabled="true" />
      <dx-pager
        :visible="true"
        :allowed-page-sizes="pageSize"
        display-mode="full"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="false"
      />
    
      <dx-filter-row :visible="true" />
      <dx-filter-panel :visible="showFilterPanel" />
      <dx-sorting mode="multiple" />
      <dx-state-storing
        :enabled="true"
        type="custom"
        :customLoad="stateLoad"
        :customSave="stateSave"
      />

      <dx-column
        data-field="active"
        :caption="$t('notifications.switch')"
        :width="90"
        data-type="boolean"
      />

      <dx-column
        data-field="name"
        :caption="$t('notifications.name')"
        data-type="string"
      />

      <dx-column
        data-field="notificationType.id"
        :caption="$t('notifications.notiftypename')"
        data-type="string"
        :width="200"
        :allow-header-filtering="true"
      >
        <dx-lookup
          :data-source="notificationTypes"
          value-expr="id"
          display-expr="name"
        />
      </dx-column>

      <dx-column
        data-field="description"
        :caption="$t('notifications.description')"
        data-type="string"
        :width="300"
      />
    </dx-data-grid>

    <CustomColumnChooser
      :visible="visible"
      :columns="columns"
      :onApply="onApply"
      :onHiding="onHiding"
    />
  </div>
</template>

<script>
import { mapCacheActions } from "vuex-cache";

import baseView from "../base.vue";
import appInfo from "../../app-info";

import DxDataGrid, {
  DxColumn, DxFilterRow, DxLookup, DxFilterPanel, DxStateStoring, DxPaging, DxPager, DxSorting
} from "devextreme-vue/data-grid";
import { DxButton } from 'devextreme-vue';

import CustomColumnChooser from "@/components/column-chooser/CustomColumnChooser.vue";

import { userNotificationsVPage } from "@/services/api";
import { AclHandler } from "@/services/acl-handler";

import { DataDrid } from "@/mixins/dataDridMixin";

import {
  PAGE_HEADER_HEIGHT,
  PAGE_PADDING,
  PAGE_TOOLBARS_HEIGHT,
  PAGE_FOOTER_HEIGHT,
  GRID_HEADER_HEIGHT,
  GRID_FILTER_ROW_HEIGHT,
  GRID_FILTER_PANEL_HEIGHT,
  GRID_PAGER_HEIGHT
} from '@/shared/consts/layout';

import _ from "underscore";

export const dictDataSourceConfig = (data) => ({
  store: { data, type: "array", key: "id" },
  map: rec => ({ value: rec.id, ...rec })
});

// 
// TODO
// 
// рефактор каждой странички уведомлений
// 

export default {
  extends: baseView,
  mixins: [DataDrid],
  components: {
    DxDataGrid, DxColumn, DxFilterRow, DxFilterPanel, DxButton, DxLookup,
    DxStateStoring, CustomColumnChooser, DxPaging, DxPager, DxSorting
  },
  data() {
    return {
      isCloudMode: this.$store.getters.isCloudMode,
      remoteOperations: { groupPaging: true },
      dataSourceConfig: { store: this.createDataSource(userNotificationsVPage) },
      notificationTypes: null
    };
  },
  computed: {
    appUser() {
      return this.$store.getters.user;
    },
    aclUser() {
      return new AclHandler(this.appUser, this.isCloudMode);
    },
    showFilterPanel() {
      return this.aclUser.hasGrantForOperation('BTN_CLEAR_FILTER', 'EXECUTE') || false;
    },
    sumGap() {
      return PAGE_HEADER_HEIGHT + PAGE_PADDING + PAGE_TOOLBARS_HEIGHT + PAGE_FOOTER_HEIGHT;
    },
    gridHeight() {
      const appHeightSize = this.$store.getters.appContainerSize.height;
      return (appHeightSize || 800) - this.sumGap;
    },
    gridBodyHeight() {
      return this.gridHeight - GRID_HEADER_HEIGHT - GRID_FILTER_ROW_HEIGHT - (this.showFilterPanel ? GRID_FILTER_PANEL_HEIGHT : 0) - GRID_PAGER_HEIGHT;
    },
  },
  methods: {
    ...mapCacheActions(['getNotificationTypes']),
    onEditorPreparing(e) {
      this.applyFilterRowTimeout(e);
    },
    onContentReady(e) {
      this.addFitToPageHeightButton(e);

      if (!this.$route.query._flash) {
        return;
      }
      const id = parseInt(this.$route.query._flash, 10);
      const query = _.extend({}, this.$route.query);
      delete query._flash;
      this.$router.replace({ query });
      setTimeout(() => e.component.selectRows([ id ]));
    },
    onRowClick(e) {
      if (window.getSelection().focusOffset !== window.getSelection().anchorOffset)
        return;

      if (e.event.metaKey || e.event.ctrlKey) {
        const routeData = this.$router.resolve({
          name: "notification",
          params: { id: e.data.id, type: e.data.notificationType.code }
        });
        window.open(routeData.href, "_blank");
      } else {
        this.$router.push({
          name: "notification",
          params: { id: e.data.id, type: e.data.notificationType.code }
        });
      }
    },
    onCreate() {
      this.$router.push({ name: "notification" });
    },
  },
  async created() {
    this.gridStateKey = 'gridNotifications';

    document.title = `${appInfo.title} - ${this.$t('notifications.title')}`;
    this.$store.commit("setBreadcrumb", [{
      link: "/notifications",
      title: this.$t('notifications.title')
    }]);
    this.notificationTypes = dictDataSourceConfig(await this.getNotificationTypes());
  },
  beforeDestroy() {
    const state = this.dataGrid.state();
    this.stateSave(state);
  },
};
</script>

<style lang="scss" scoped>
.app-buttons {
  .dx-icon {
    margin-right: 5px;
  }
  .subscribers-popup {
    margin-left: -32px;
  }
}
</style>
